// Home — the first destination for every persona (BRD 4.5; home.md §1.1; DC-43; V1-INTERACTIONS §4.26). One
// architecture with security-trimmed widgets: `home.open` composes the union of the widgets the person's roles grant,
// in fixed order, and this page draws them. A multi-role person sees each widget once (DC-52). The page renders inside
// the shell's content slot and draws no chrome of its own; the shell's nav, top bar and signed-in person sit around it.
//
// The title is his locator *Home* (the greeting of DC-43 waits for his words, Q-D2). The meta line is the person's role
// words, the scope words the server sends (his matrix words) and the count of their Prioritized actions (D-6, HO-28).
// Loading is the header's skeleton and the grid's skeleton cards at their final height (DC-25); a failed read is the
// error strip with Retry.

import { useEffect } from "react";
import { homeOpen, type HomeOpenOutput } from "@/api/tools";
import { errorText, useRead } from "@/api/useTool";
import { ErrorStrip, NAV_ITEM_LABELS, PageHeader, Stack, Widget, Widgets, METRIC_LABELS, SEATS } from "@/mc";
import type { Route } from "@/router";
import { useViewState } from "@/shell/viewState";
import { HomeWidgets } from "@/views/home/widgets";
import { McPage, isCapturePage, settled, useCaptureSettle } from "@/shell/page";

/** Polling stops while a frame is photographed; a write anywhere invalidates ["home"] at once (useWrite). */
const POLL_MS = 60_000;

/** The meta line: the role words, the scope words not already said, then "{n} Prioritized actions". */
export function metaOf(home: Pick<HomeOpenOutput, "role_words" | "scope_words" | "actions_open">): string[] {
  return [
    ...home.role_words,
    ...home.scope_words.filter((word) => !home.role_words.includes(word)),
    `${home.actions_open} ${METRIC_LABELS.prioritizedActions}`,
  ];
}

function HomeScreen() {
  const { register } = useViewState();
  const settle = useCaptureSettle();
  const q = useRead(["home"], () => settled(settle, homeOpen({})), { refetchInterval: isCapturePage() ? false : POLL_MS });
  const home = q.data;
  useEffect(() => {
    register({
      view: "home",
      ids: {},
      summary: home
        ? { loading: false, count: home.actions_open, state: home.widgets.map((w) => w.key).join(",") }
        : { loading: q.isLoading },
    });
  }, [register, q.isLoading, home]);
  return (
    <Stack gap={20}>
      <PageHeader
        title={NAV_ITEM_LABELS.home}
        loading={q.isLoading}
        meta={home ? metaOf(home) : undefined}
      />
      {q.isError ? (
        <ErrorStrip message={errorText(q.error)} onRetry={() => void q.refetch()} retrying={q.isFetching} live  />
      ) : null}
      {q.isLoading ? (
        <Widgets>
          <Widget id="action_center_preview" kind="actionCenter" roles={SEATS} title={METRIC_LABELS.actionCenter} level={2} loading={240} />
        </Widgets>
      ) : null}
      {home ? <HomeWidgets widgets={home.widgets} /> : null}
    </Stack>
  );
}

/** The route's view is "home"; Home reads no route part or query. */
export function Home(_props: { route?: Route | undefined }) {
  return (
    <McPage>
      <HomeScreen />
    </McPage>
  );
}

export default Home;
